package org.multiplatform.kotfolio.components

import androidx.compose.runtime.*
import org.multiplatform.kotfolio.util.Constants.AMBER_500
import org.multiplatform.kotfolio.util.Constants.FONT_FAMILY
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.animation.Keyframes
import com.varabyte.kobweb.silk.style.animation.toAnimation
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import kotlin.time.Duration.Companion.milliseconds

// Colors picked to match the provided reference image
private val DARCULA_ORANGE = Color.rgb(204, 120, 50)
private val DARCULA_YELLOW = Color.rgb(255, 198, 109)
private val MINT_GREEN = Color.rgb(118, 209, 160)
private val DARCULA_GRAY = Color.rgb(128, 128, 128)
private val DARCULA_BLUE = Color.rgb(104, 151, 187)
private val IDE_BACKGROUND = Color.rgb(30, 24, 20)
private val SIDEBAR_COLOR = Color.rgb(25, 20, 16)

val CodeWindowBlinkKeyframes = Keyframes {
    0.percent { Modifier.opacity(1) }
    50.percent { Modifier.opacity(0) }
    100.percent { Modifier.opacity(1) }
}

@Composable
fun CodeWindow() {
    var visibleLines by remember { mutableStateOf(0) }

    // Typing animation logic
    var isHydrated by remember { mutableStateOf(false) }
    LaunchedEffect(Unit) {
        isHydrated = true
        delay(800.milliseconds) // Slightly longer initial delay for safety
        for (i in 1..15) {
            visibleLines = i
            delay(120.milliseconds) // Snappier typing
        }
    }

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .height(550.px)
            .backgroundColor(IDE_BACKGROUND)
            .borderRadius(16.px)
            .boxShadow(offsetX = 0.px, offsetY = 25.px, blurRadius = 50.px, color = Color.rgba(0, 0, 0, 0.6f))
            .overflow(Overflow.Hidden)
            .fontFamily(FONT_FAMILY)
            .border(1.px, LineStyle.Solid, Color.rgba(255, 255, 255, 0.05f)),
    ) {
        Column(modifier = Modifier.fillMaxWidth().fillMaxHeight()) {
            // Title Bar
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(44.px)
                    .backgroundColor(Color.rgb(45, 38, 34))
                    .padding(leftRight = 20.px)
                    .styleModifier {
                        property("border-bottom", "1px solid rgba(255, 255, 255, 0.1)")
                    },
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.SpaceBetween,
            ) {
                Row(horizontalArrangement = Arrangement.spacedBy(10.px)) {
                   WindowDot(
                        Color.rgb(
                            255,
                            95,
                            87
                        )
                    )
                   WindowDot(
                        Color.rgb(
                            255,
                            189,
                            46
                        )
                    )
                   WindowDot(
                        Color.rgb(
                            39,
                            201,
                            63
                        )
                    )
                }

                Span(
                    attrs = Modifier
                        .color(Color.rgb(180, 180, 180))
                        .fontSize(13.px)
                        .fontWeight(FontWeight.Medium)
                        .toAttrs(),
                ) {
                    Text("Developer.kt")
                }

                Box(modifier = Modifier.width(56.px))
            }

            Row(modifier = Modifier.fillMaxWidth().weight(1f)) {
                // File Explorer Sidebar
                Column(
                    modifier = Modifier
                        .width(160.px)
                        .fillMaxHeight()
                        .backgroundColor(SIDEBAR_COLOR)
                        .padding(top = 20.px, leftRight = 12.px)
                        .styleModifier {
                            property("border-right", "1px solid rgba(255, 255, 255, 0.05)")
                        },
                ) {
                   SidebarItem(
                        "src",
                        isFolder = true,
                        isOpen = true
                    )
                   SidebarItem(
                        "main",
                        isFolder = true,
                        isOpen = true,
                        indent = 1
                    )
                   SidebarItem(
                        "kotlin",
                        isFolder = true,
                        isOpen = true,
                        indent = 2
                    )
                   SidebarItem(
                        "Developer.kt",
                        isSelected = true,
                        indent = 3
                    )
                   SidebarItem(
                        "Constants.kt",
                        indent = 3
                    )
                   SidebarItem(
                        "Index.kt",
                        indent = 3
                    )
                }

                // Code Content
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .fillMaxHeight()
                        .padding(topBottom = 24.px, leftRight = 16.px)
                        .whiteSpace(WhiteSpace.PreWrap)
                        .overflow(Overflow.Auto),
                ) {
                    // Line Numbers
                    Column(
                        modifier = Modifier.margin(right = 16.px),
                        horizontalAlignment = Alignment.End,
                    ) {
                        (1..15).forEach {
                            if (it <= visibleLines) {
                                Span(
                                    attrs = Modifier
                                        .color(Color.rgb(80, 70, 65))
                                        .fontSize(12.px)
                                        .fontFamily("Fira Code", "Courier New", "monospace")
                                        .lineHeight(1.6)
                                        .toAttrs(),
                                ) { Text("$it") }
                                Br()
                            }
                        }
                    }

                    // Code
                    Column(modifier = Modifier.fillMaxWidth()) {
                        if (visibleLines >= 1) {
                           CodeLine(
                                indent = 0,
                                showCursor = visibleLines == 1
                            ) {
                               Keyword("class")
                               TypeName(" Developer")
                               CodeText(
                                    " {",
                                    color = Colors.White
                                )
                            }
                        }
                        if (visibleLines >= 2) {
                           CodeLine(
                                indent = 1,
                                showCursor = visibleLines == 2
                            ) {
                               Keyword("val")
                               CodeText(
                                    " role = ",
                                    color = Colors.White
                                )
                               StringLiteral(
                                    "\"Senior Android Developer\""
                                )
                            }
                        }
                        if (visibleLines >= 3) {
                           CodeLine(
                                indent = 1,
                                showCursor = visibleLines == 3
                            ) {
                               Keyword("val")
                               CodeText(
                                    " exp = ",
                                    color = Colors.White
                                )
                               NumberLiteral(
                                    "8"
                                )
                               CodeText(
                                    ".years",
                                    color = Colors.White
                                )
                            }
                        }
                        if (visibleLines >= 4) {
                           CodeLine(
                                indent = 1,
                                showCursor = visibleLines == 4
                            ) {
                               Keyword("val")
                               CodeText(
                                    " focus = listOf(",
                                    color = Colors.White
                                )
                            }
                        }
                        if (visibleLines >= 5) {
                           CodeLine(
                                indent = 2,
                                showCursor = visibleLines == 5
                            ) {
                               StringLiteral(
                                    "\"Jetpack Compose\""
                                )
                               CodeText(
                                    ", ",
                                    color = Colors.White
                                )
                            }
                        }
                        if (visibleLines >= 6) {
                           CodeLine(
                                indent = 2,
                                showCursor = visibleLines == 6
                            ) {
                               StringLiteral(
                                    "\"KMP\""
                                )
                               CodeText(
                                    ", ",
                                    color = Colors.White
                                )
                               StringLiteral(
                                    "\"Clean Architecture\""
                                )
                            }
                        }
                        if (visibleLines >= 7) {
                           CodeLine(
                                indent = 1,
                                showCursor = visibleLines == 7
                            ) {
                               CodeText(
                                    ")",
                                    color = Colors.White
                                )
                            }
                        }
                        if (visibleLines >= 8) Br()
                        if (visibleLines >= 9) {
                           CodeLine(
                                indent = 1,
                                showCursor = visibleLines == 9
                            ) {
                               Keyword("fun")
                               FunctionName(
                                    " launchProject"
                                )
                               CodeText(
                                    "(name: ",
                                    color = Colors.White
                                )
                               TypeName("String")
                               CodeText(
                                    ") {",
                                    color = Colors.White
                                )
                            }
                        }
                        if (visibleLines >= 10) {
                           CodeLine(
                                indent = 2,
                                showCursor = visibleLines == 10
                            ) {
                               Keyword("when")
                               CodeText(
                                    " (name) {",
                                    color = Colors.White
                                )
                            }
                        }
                        if (visibleLines >= 11) {
                           CodeLine(
                                indent = 3,
                                showCursor = visibleLines == 11
                            ) {
                               StringLiteral(
                                    "\"AnimeLoom\""
                                )
                               CodeText(
                                    " -> ",
                                    color = Colors.White
                                )
                               FunctionName(
                                    "deploy"
                                )
                               CodeText(
                                    "()",
                                    color = Colors.White
                                )
                            }
                        }
                        if (visibleLines >= 12) {
                           CodeLine(
                                indent = 3,
                                showCursor = visibleLines == 12
                            ) {
                               StringLiteral(
                                    "\"Currentzy\""
                                )
                               CodeText(
                                    " -> ",
                                    color = Colors.White
                                )
                               FunctionName(
                                    "optimize"
                                )
                               CodeText(
                                    "()",
                                    color = Colors.White
                                )
                            }
                        }
                        if (visibleLines >= 13) {
                           CodeLine(
                                indent = 2,
                                showCursor = visibleLines == 13
                            ) {
                               CodeText(
                                    "}",
                                    color = Colors.White
                                )
                            }
                        }
                        if (visibleLines >= 14) {
                           CodeLine(
                                indent = 1,
                                showCursor = visibleLines == 14
                            ) {
                               CodeText(
                                    "}",
                                    color = Colors.White
                                )
                            }
                        }
                        if (visibleLines >= 15) {
                           CodeLine(
                                indent = 0,
                                showCursor = true
                            ) {
                               CodeText(
                                    "}",
                                    color = Colors.White
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun BlinkingCursor() {
    Box(
        modifier = Modifier
            .width(10.px)
            .height(20.px)
            .backgroundColor(AMBER_500)
            .margin(left = 4.px)
            .animation(
               CodeWindowBlinkKeyframes.toAnimation(
                    duration = 1.s,
                    iterationCount = AnimationIterationCount.Infinite,
                    timingFunction = AnimationTimingFunction.EaseInOut,
                ),
            ),
    )
}

@Composable
private fun SidebarItem(
    name: String,
    isFolder: Boolean = false,
    isOpen: Boolean = false,
    isSelected: Boolean = false,
    indent: Int = 0,
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(left = (indent * 12).px)
            .padding(topBottom = 4.px)
            .backgroundColor(if (isSelected) Color.rgba(255, 255, 255, 0.05f) else Colors.Transparent)
            .borderRadius(4.px),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Span(
            attrs = Modifier
                .margin(right = 8.px)
                .fontSize(10.px)
                .styleModifier {
                    if (isFolder) {
                        property("color", if (isOpen) "#FFC66D" else "#808080")
                    } else {
                        property("color", "#A97BFF")
                    }
                }
                .toAttrs(),
        ) {
            Text(if (isFolder) (if (isOpen) "▼" else "▶") else "•")
        }

        Span(
            attrs = Modifier
                .color(if (isSelected) Colors.White else Color.rgb(180, 180, 180))
                .fontSize(11.px)
                .fontWeight(if (isSelected) FontWeight.Bold else FontWeight.Normal)
                .toAttrs(),
        ) {
            Text(name)
        }
    }
}

@Composable
private fun WindowDot(color: Color) {
    Box(
        modifier = Modifier
            .size(12.px)
            .borderRadius(50.percent)
            .backgroundColor(color),
    )
}

@Composable
private fun CodeLine(indent: Int, showCursor: Boolean = false, content: @Composable () -> Unit) {
    Row(
        modifier = Modifier
            .padding(left = (indent * 20).px)
            .lineHeight(1.6),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        content()
        if (showCursor) {
           BlinkingCursor()
        }
    }
}

@Composable
private fun Keyword(text: String) {
    Span(attrs = Modifier.color(DARCULA_ORANGE).fontSize(14.px).fontWeight(FontWeight.Bold).toAttrs()) { Text(text) }
}

@Composable
private fun FunctionName(text: String) {
    Span(attrs = Modifier.color(DARCULA_YELLOW).fontSize(14.px).toAttrs()) { Text(text) }
}

@Composable
private fun TypeName(text: String) {
    Span(attrs = Modifier.color(DARCULA_YELLOW).fontSize(14.px).toAttrs()) { Text(text) }
}

@Composable
private fun StringLiteral(text: String) {
    Span(attrs = Modifier.color(MINT_GREEN).fontSize(14.px).toAttrs()) { Text(text) }
}

@Composable
private fun NumberLiteral(text: String) {
    Span(attrs = Modifier.color(DARCULA_BLUE).fontSize(14.px).toAttrs()) { Text(text) }
}

@Composable
private fun Comment(text: String) {
    Span(attrs = Modifier.color(DARCULA_GRAY).fontSize(14.px).fontStyle(FontStyle.Italic).toAttrs()) { Text(text) }
}

@Composable
private fun CodeText(text: String, color: Color) {
    Span(attrs = Modifier.color(color).fontSize(14.px).toAttrs()) { Text(text) }
}
